import {
  Button, LinkAsButton, Modal,
} from '@printdeal/ui-library';
import React, { useState } from 'react';
import clsx from 'clsx';
import {
  AddressFormSubmitButton, AddressListView, CreateAddressView, UpdateAddressView,
} from '@printdeal/address-frontend';
import { FormattedMessage, useIntl } from 'react-intl';
import { BillingAddressPanel, ErrorAlert } from '@printdeal/checkout-frontend';
import useSetBillingAddressMutation from './queries/useSetBillingAddressMutation';
import BillingAddressPanelFooter from './BillingAddressPanelFooter';

interface Props {
  orderId: string;
  isOpen: boolean;
  onChangeSuccess: () => void;
  onClose: () => void;
}

const BillingAddressModal = ({
  orderId,
  isOpen,
  onChangeSuccess,
  onClose,
}: Props) => {
  const intl = useIntl();
  const setBillingAddressMutation = useSetBillingAddressMutation({ orderId });

  const [view, setView] = useState<'list' | 'create' | 'edit'>('list');
  const [mutatingAddressId, setMutatingAddressId] = useState<string | undefined>();
  const [showSuccessAlertForCreatedAddressId, setShowSuccessAlertForCreatedAddressId] = useState<string | undefined>();
  const [showSuccessAlertForUpdatedAddressId, setShowSuccessAlertForUpdatedAddressId] = useState<string | undefined>();
  const [deleteAddressModalIsOpen, setDeleteAddressModalIsOpen] = useState<string | undefined>();

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => {
        setBillingAddressMutation.reset();

        setView('list');
        setMutatingAddressId(undefined);
        setShowSuccessAlertForCreatedAddressId(undefined);
        setShowSuccessAlertForUpdatedAddressId(undefined);

        onClose();
      }}
      size="lg"
      dismissLabel={intl.formatMessage({
        id: 'general.modal.close',
        defaultMessage: 'Close',
      })}
      title={intl.formatMessage({
        id: 'checkout.selectBillingAddress',
        defaultMessage: 'Select billing address',
      })}
    >
      <Modal.Content className={clsx('pd-h-[90vh]', { 'pd-p-0': view === 'list' })}>
        {view === 'list' && (
          <AddressListView
            className="pd-p-4"
            showSuccessAlertForCreatedAddressId={showSuccessAlertForCreatedAddressId}
            showSuccessAlertForUpdatedAddressId={showSuccessAlertForUpdatedAddressId}
            renderAddressPanel={({ address, ...data }, focusedAddressId) => (
              <BillingAddressPanel
                address={address}
                isDefaultBillingAddress={data.isDefaultBillingAddress}
                isDefaultShippingAddress={data.isDefaultShippingAddress}
                focusedAddressId={focusedAddressId}
                setView={setView}
                setMutatingAddressId={setMutatingAddressId}
                setShowSuccessAlertForCreatedAddressId={setShowSuccessAlertForCreatedAddressId}
                setShowSuccessAlertForUpdatedAddressId={setShowSuccessAlertForUpdatedAddressId}
                deleteAddressModalIsOpen={deleteAddressModalIsOpen}
                setDeleteAddressModalIsOpen={setDeleteAddressModalIsOpen}
                footer={(
                  <BillingAddressPanelFooter
                    address={address}
                    setBillingAddressMutation={setBillingAddressMutation}
                    setView={setView}
                    mutatingAddressId={mutatingAddressId}
                    setMutatingAddressId={setMutatingAddressId}
                    setDeleteAddressModalIsOpen={setDeleteAddressModalIsOpen}
                    onChangeSuccess={onChangeSuccess}
                    onChangeError={() => {}}
                  />
                )}
              />
            )}
            alert={setBillingAddressMutation.isError && (
              <ErrorAlert
                className="pd-m-0"
                scrollIntoView
                sentryException={{
                  e: setBillingAddressMutation.error,
                  tags: {
                    fileLocation: 'ShippingAddressModal.tsx',
                    featureFunction: 'ShippingAddressModal',
                  },
                  fingerPrint: ['order-detail-page', 'update-shipping-address'],
                }}
              >
                <FormattedMessage
                  id="checkout.shippingAddress.error"
                  defaultMessage="An error occurred while updating the shipping address. Please try again."
                />
              </ErrorAlert>
            )}
          />
        )}

        {view === 'create' && (
          <CreateAddressView
            onSubmitSuccess={(address) => {
              setShowSuccessAlertForCreatedAddressId(address.id);
              setView('list');
            }}
          />
        )}

        {view === 'edit' && (
          <UpdateAddressView
            mutatingAddressId={mutatingAddressId}
            onSubmitSuccess={() => {
              setShowSuccessAlertForUpdatedAddressId(mutatingAddressId);
              setView('list');
            }}
          />
        )}
      </Modal.Content>

      <Modal.Footer>
        {view === 'list' && (
          <div className="pd-flex pd-justify-end">
            <Button variant="secondary" iconBefore="plus" onClick={() => setView('create')}>
              <FormattedMessage
                id="checkout.addAddress"
                defaultMessage="Add address"
              />
            </Button>
          </div>
        )}

        {(view === 'create' || view === 'edit') && (
          <div className="pd-flex pd-items-center pd-justify-between">
            <LinkAsButton
              className="pd-text-sm"
              iconBefore="chevron-left"
              variant="secondary"
              onClick={() => {
                setShowSuccessAlertForCreatedAddressId(undefined);
                setShowSuccessAlertForUpdatedAddressId(undefined);
                setView('list');
              }}
            >
              <FormattedMessage
                id="checkout.backToAddressList"
                defaultMessage="Back to address list"
              />
            </LinkAsButton>

            <AddressFormSubmitButton />
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default BillingAddressModal;
